import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Package, CaretDown, CaretUp, CheckCircle, ArrowSquareOut, DownloadSimple, Lock } from "@phosphor-icons/react";
import { Pill } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { KINDS, KindPill, err } from "./libraryUtils";

const COLLAPSE_KEY = "hs_library_bundles_collapsed";
const kindCount = (by_kind) => KINDS.filter((k) => by_kind[k.key]).map((k) => `${by_kind[k.key]} ${by_kind[k.key] === 1 ? k.label : k.plural}`);

/** "Start with a bundle": one automatic bundle per trade (its items + the general essentials); one tap copies what the workspace is missing. */
export function StarterBundles({ canEdit, onCopied }) {
  const [bundles, setBundles] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(() => API.get("/hs/library/bundles").then((r) => setBundles(r.data.bundles || [])).catch(() => setBundles([])), []);
  useEffect(() => { load(); }, [load]);
  const toggle = () => { setCollapsed((c) => { localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1"); return !c; }); };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hs/library/bundles/use", { trade: open.trade });
      setResult(data); toast.success(data.message); load(); onCopied?.();
    } catch (e) { err(e, "Couldn't copy the bundle"); } finally { setBusy(false); }
  };
  if (!bundles || !bundles.length) return null;
  return (
    <section className="rounded-lg border border-border bg-card" data-testid="hs-library-bundles">
      <button type="button" onClick={toggle} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="hs-library-bundles-toggle">
        <Package size={18} weight="fill" className="text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Start with a bundle</p>
          {collapsed ? <p className="text-[11px] text-muted-foreground truncate">{bundles.length} trade bundles — one tap copies the policy, COSHH, fire, RAMS and talks a new workspace needs on day one</p>
            : <p className="text-[11px] text-muted-foreground">One tap copies everything a workspace in your trade needs on day one — the general essentials plus that trade's RAMS, assessments, permits and talks. Items you already hold are skipped, never duplicated.</p>}
        </div>
        {collapsed ? <CaretDown size={16} className="text-muted-foreground" /> : <CaretUp size={16} className="text-muted-foreground" />}
      </button>
      {!collapsed && (
        <div className="flex gap-3 overflow-x-auto px-4 pb-4" data-testid="hs-library-bundle-list">
          {bundles.map((b) => (
            <div key={b.trade} className={`shrink-0 w-72 rounded-lg border p-3 flex flex-col gap-2 ${b.essentials ? "border-primary/50 bg-primary/5" : "border-border"}`} data-testid={`hs-library-bundle-${b.trade}`}>
              <div>
                <p className="text-sm font-semibold leading-tight">{b.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{b.total} item{b.total === 1 ? "" : "s"} · {kindCount(b.by_kind).join(" · ")}</p>
              </div>
              <div className="mt-auto space-y-1.5">
                {b.missing === 0 && b.not_allowed === 0 ? <Pill tone={TONE_CLASSES.ok} testid={`hs-library-bundle-done-${b.trade}`}><CheckCircle size={11} className="mr-1" /> All in your workspace</Pill>
                  : <p className="text-[11px] text-muted-foreground" data-testid={`hs-library-bundle-progress-${b.trade}`}>{b.in_workspace} of {b.total} in your workspace{b.not_allowed ? ` · ${b.not_allowed} for an H&S Manager` : ""}</p>}
                <Button size="sm" variant={b.missing === 0 ? "outline" : "default"} className="h-7 text-xs w-full" disabled={!canEdit} onClick={() => { setResult(null); setOpen(b); }} data-testid={`hs-library-bundle-use-${b.trade}`}>
                  {b.missing === 0 ? "Review what's included" : <><DownloadSimple size={12} className="mr-1" /> Copy the {b.missing} missing</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => { if (!o) { setOpen(null); setResult(null); } }}>
        <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="hs-library-bundle-dialog">
          {open && !result && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Package size={18} weight="fill" className="text-primary" /> {open.title}</DialogTitle>
                <DialogDescription>{open.missing > 0 ? `${open.missing} item${open.missing === 1 ? "" : "s"} will be copied into your workspace as drafts to personalise. ` : "Everything in this bundle is already in your workspace. "}{open.in_workspace > 0 && open.missing > 0 ? `${open.in_workspace} you already hold will be skipped.` : ""}</DialogDescription>
              </DialogHeader>
              <ul className="divide-y divide-border rounded-md border border-border" data-testid="hs-library-bundle-items">
                {open.items.map((it) => (
                  <li key={it.id} className={`flex flex-wrap items-center gap-2 px-3 py-1.5 text-sm ${it.in_workspace || !it.allowed ? "opacity-60" : ""}`} data-testid={`hs-library-bundle-item-${it.id}`}>
                    <KindPill kind={it.kind} />
                    <span className="flex-1 min-w-[160px]">{it.title}</span>
                    {it.in_workspace ? <Pill tone={TONE_CLASSES.ok}>Already in your workspace</Pill>
                      : !it.allowed ? <Pill tone={TONE_CLASSES.none}><Lock size={10} className="mr-1" /> H&amp;S Manager only</Pill>
                        : <Pill tone={TONE_CLASSES.soon}>Will be copied</Pill>}
                  </li>
                ))}
              </ul>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(null)} data-testid="hs-library-bundle-cancel">Cancel</Button>
                {open.missing > 0 && <Button disabled={busy} onClick={run} data-testid="hs-library-bundle-confirm"><DownloadSimple size={14} className="mr-1" /> {busy ? "Copying…" : `Copy ${open.missing} into my workspace`}</Button>}
              </DialogFooter>
            </>
          )}
          {result && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-emerald-600" /> {result.title} — done</DialogTitle>
                <DialogDescription data-testid="hs-library-bundle-result-message">{result.message}</DialogDescription>
              </DialogHeader>
              {result.copied.length > 0 && (
                <ul className="divide-y divide-border rounded-md border border-border" data-testid="hs-library-bundle-copied">
                  {result.copied.map((it) => (
                    <li key={it.id} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-sm" data-testid={`hs-library-bundle-copied-${it.id}`}>
                      <KindPill kind={it.kind} />
                      <span className="flex-1 min-w-[160px]">{it.title} <span className="text-[11px] text-muted-foreground">→ {it.label}</span></span>
                      <Link to={it.url} className="text-xs text-primary hover:underline inline-flex items-center gap-1" onClick={() => { setOpen(null); setResult(null); }} data-testid={`hs-library-bundle-open-${it.id}`}>Open <ArrowSquareOut size={12} /></Link>
                    </li>
                  ))}
                </ul>
              )}
              {result.failed?.length > 0 && <p className="text-xs text-red-600" data-testid="hs-library-bundle-failed">Couldn't copy: {result.failed.map((f) => f.title).join(", ")}</p>}
              <DialogFooter><Button onClick={() => { setOpen(null); setResult(null); }} data-testid="hs-library-bundle-close">Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
